import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, HostBinding, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ListboxModule } from 'primeng/listbox';
import { MessageModule } from 'primeng/message';
import { finalize, Subscription, take } from 'rxjs';
import { collapseAnimation } from '../../animations/collapse.animation';
import { ISelectButtonColoredItem, SelectButtonColoredComponent } from '../../components/select-button-colored/select-button-colored.component';
import { SessionInfoIconsComponent } from '../../components/session-info-icons/session-info-icons.component';
import { CURRENCIES, Currency } from '../../enums/price/currencies.enum';
import { SessionLecturerState } from '../../enums/session-group/session-lecturer-states.enum';
import { SessionLecturerType } from '../../enums/session-group/session-lecturer-types.enum';
import { Color } from '../../enums/utils/colors';
import { ISessionGroup } from '../../models/session-group/session-group.model';
import { ISessionLecturerAttendancePostData } from '../../models/session-group/session-lecturer-attendance-post-data.model';
import { ISessionLecturer } from '../../models/session-group/session-lecturer.model';
import { ISession } from '../../models/session-group/session.model';
import { ILecturer } from '../../models/user/lecturer/lecturer.model';
import { LocalizedDatePipe } from '../../pipes/localized-date.pipe';
import { UserNameOrEmailPipe } from '../../pipes/user-name-or-email.pipe';
import { ISessionLecturerAttendanceSession, ISessionLecturerAttendanceSessionGroup, SessionGroupsService } from '../../services/entities/session-groups/session-groups.service';
import { ILecturersState, LecturersStoreService } from '../../services/store/lecturers-store.service';
import { SessionsLecturerStoreService } from '../../services/store/sessions-lecturer-store.service';
import { UserStoreService } from '../../services/store/user-store.service';
import { UtilsService } from '../../services/utils.service';
import { ModalHeaderComponent } from '../components/modal-header/modal-header.component';
import { Modal } from '../modal';
import { PricePipe } from '../../pipes/price.pipe';
import { ModalService } from '../../services/modal.service';
import { SessionAttendanceModalComponent } from '../session-attendance-modal/session-attendance-modal.component';
import { SelectedOrgService } from '../../services/selected-org.service';
import { ShopItemHelperService } from '../../services/helpers/shop-item-helper.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NumberInputDirective } from '../../directives/number-input.directive';
import { SessionLecturerAttendanceInputGroupComponent } from 'src/app/pages/admin/org-admin/components/modals/session-detail-card-modal/session-lecturer-attendance-input-group/session-lecturer-attendance-input-group.component';
import { IModalFooterMenuItem, ModalFooterMenuComponent } from '../../components/modal-footer-menu/modal-footer-menu.component';
import { ICallablesSessionsGetForSessionDetailCardModalResult, SessionsService } from '../../services/entities/sessions/sessions.service';
import { StoreService } from '../../services/store/store.service';



@Component({
  selector: 'app-session-lecturer-attendance-modal',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, TranslateModule,
    ModalHeaderComponent, SessionInfoIconsComponent, 

    SessionLecturerAttendanceInputGroupComponent, ModalFooterMenuComponent,
  ],
  templateUrl: './session-lecturer-attendance-modal.component.html',
  styleUrls: ['./session-lecturer-attendance-modal.component.scss'],
  animations: [ collapseAnimation ]
})
export class SessionLecturerAttendanceModalComponent extends Modal implements OnInit, OnDestroy {
  SessionLecturerType = SessionLecturerType;

  @Input({required: true}) sessionId: number | null = null;
  @Input({required: true}) type: SessionLecturerType | undefined;

  footerMenuItems: IModalFooterMenuItem[] = [];

  sessionData: ICallablesSessionsGetForSessionDetailCardModalResult | null = null;
  @HostBinding('class.loading-box') fetching: boolean = false;
  private _sessionLecturerAttendanceInputGroup: SessionLecturerAttendanceInputGroupComponent | undefined;
  get sessionLecturerAttendanceInputGroup(): SessionLecturerAttendanceInputGroupComponent | undefined { return this._sessionLecturerAttendanceInputGroup; }
  @ViewChild('sessionLecturerAttendanceInputGroup') set sessionLecturerAttendanceInputGroup(val: SessionLecturerAttendanceInputGroupComponent) {
    this._sessionLecturerAttendanceInputGroup = val;
    this.handleSessionLecturerAttendanceInputGroup();
  };

  subs: Subscription[] = [];

  constructor(
    private sessionsService: SessionsService,
    private store: StoreService,
    private modalService: ModalService,
    private utils: UtilsService
  ) {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.fetchSessionData();
  }


  private fetchSessionData() {
    if (!this.sessionId) return;

    this.fetching = true;
    // reusing this callable
    this.sessionsService.getForSessionDetailCardModal({
      sessionId: this.sessionId
    }).pipe(
      take(1)
    ).subscribe({
      next: (res) => {
        this.sessionData = res;
        this.fetching = false;
      },
      error: (err) => {
        this.fetching = false;
        this.sessionData = null;
      }
    })
  }

  private handleSessionLecturerAttendanceInputGroup() {
    this.initFooterMenuItems(this.sessionLecturerAttendanceInputGroup);
    if (this.sessionLecturerAttendanceInputGroup) {
      this.subs.push(
        this.sessionLecturerAttendanceInputGroup.FG.valueChanges.subscribe((data) => {
          this.initFooterMenuItems(this.sessionLecturerAttendanceInputGroup);
        }),
        // this.sessionLecturerAttendanceInputGroup.FG.statusChanges.subscribe((status) => {
        //   console.log('statusChanges', status);
        //   this.initFooterMenuItems(this.sessionLecturerAttendanceInputGroup!);
        // }),
      );
    }
  }

  private initFooterMenuItems(c: SessionLecturerAttendanceInputGroupComponent | undefined ) {
    const FG = c?.FG;
    const items: IModalFooterMenuItem[] = [];

    if (FG?.dirty) {
      items.push({
        icon: 'pi pi-times',
        translationText: 'shared.footer-menu.item.close-no-save.label',
        action: () => {
          this.close();
        },
      });
    } else {
      items.push({
        icon: 'pi pi-chevron-left',
        translationText: 'shared.footer-menu.item.close.label',
        action: () => {
          this.close();
        },
      });
    }

    const sessionUuid = this.sessionData?.uuid ?? this.sessionLecturerAttendanceInputGroup?.FG.controls.substitutionFG?.controls.session.value?.uuid;
    if (sessionUuid) {
      items.push({
        icon: 'pi pi-users',
        translationText: 'admin.session-lecturer-attendance-modal.sessionAttendance',
        action: () => {
          this.modalService.openSessionAttendanceModal(SessionAttendanceModalComponent, sessionUuid);
        },
      })
    }


    if (FG?.dirty) {
      items.push({
          icon: 'pi pi-save',
          translationText: 'shared.footer-menu.item.save-close.label',
          action: () => {
            const res = c?.onSubmit();
            res?.pipe(take(1)).subscribe((r) => {
              if (r) {
                this.store.actions.sessionLecturerAttendance_sessionLecturerAttendanceChanged$.next({ sessionId: this.sessionId!});
                this.close();
              }
            })
          },
          isLoading: () => c?.sending ?? false,
      });
    }

    this.footerMenuItems = items;
  }


  override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.subs.forEach(s => s.unsubscribe());
  }

}

